import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, Divider, IconButton, InputAdornment, List, ListItem, Popper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import searchIcon from '../../../assets/images/faqImage/search.svg';
import closeIcon from '../../../assets/images/faqImage/cancel.svg';
import redCloseIcon from '../../../assets/images/faqImage/red-cancel.svg';
import searchRedIcon from '../../../assets/images/faqImage/searchRed.svg';
import spinnerImg from '../../../assets/images/faqImage/spinnerImg.png';
import { useDispatch, useSelector } from "react-redux";
import { getFAQs, getFAQsAutoComplete, setFAQs, setLoading } from "../../../redux/slices/FAQSlice";
import { useTranslation } from "react-i18next";
import _debounce from 'lodash/debounce';


const languageMapper = {
  en: 'en_us',
  fr: 'fr_ca',
}

function CustomListboxComponent(props: any) {
  const { children, currentPage, totalPages, ...other } = props;
  return (
    <List {...other} style={{ maxHeight: 'none', overflow: 'visible' }}>
      {children}
      <ListItem sx={{ justifyContent: 'center', textAlign: 'center' }}>
        {currentPage < totalPages && !props.isTyping && (
          <ListItem sx={{ justifyContent: 'center', textAlign: 'center' }}>
            <Button onClick={props.loadMore} sx={{ fontWeight: '700', color: '#1460AA', fontSize: '16px', textTransform: 'none' }}>{props.label}</Button>
          </ListItem>
        )}
      </ListItem>
    </List>
  );
}

function FAQSearchBar({ fabLabel, handleSearchCallback, setOptionsHeight, optionsHeight }) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [filterError, setFilterError] = useState(false)
  const [noOptionsText, setNoOptionsText] = useState('');
  const [searchCount, setSearchCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const dispatch = useDispatch()
  const [page, setPage] = useState(1);
  const flagRef = React.useRef(true);
  const prevOptionsHeight = React.useRef(optionsHeight);

  const { i18n } = useTranslation();
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
  const isSM = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMD = useMediaQuery(theme.breakpoints.between('md', 'lg'));



  const state = useSelector(({ faq }: any) => ({ ...faq }))


  const filterOptions = (options, state) => {
    return options;
  }

  const element = document.getElementById('autocomplete-popper')
  if (element && element.clientHeight !== optionsHeight && flagRef.current) {
    setOptionsHeight(element.clientHeight)
    prevOptionsHeight.current = element.clientHeight
  }

  React.useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(setLoading())
      dispatch(getFAQsAutoComplete({ query: searchTerm, lang: languageMapper[i18n.language] }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);


  useEffect(() => {
    if (searchTerm.length === 0) {
      clearSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  useEffect(() => {
    if (state.faqs?.search_results?.length || state.auto?.length) {
      setSearchCount(state.faqs?.search_results.length)
      setFilterError(false)
    } else {
      setSearchCount(0)
      setFilterError(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.faqs?.search_results])

  const handleEnterPressSearch = (e) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      dispatch(setLoading())
      // setFilter();
      setIsTyping(false)
      dispatch(getFAQs({ query: searchTerm, page, lang: languageMapper[i18n.language] }))
    }
  }

  const clearSearch = () => {
    setSearchTerm("");
    handleSearchCallback('')
    setDebouncedSearchTerm("");
    dispatch(setFAQs({ search_results: [], page: undefined }))
    setPage(1)
    setOptionsHeight(0)
    prevOptionsHeight.current = 0
  }

  function NoOptionsText() {
    return (
      <Box display="flex" alignItems="center" color="#D8292F">
        <Alert sx={{
          padding: 0,
          margin: 0,
          bgcolor: 'background.paper'
        }} severity="error" />{noOptionsText}
      </Box>
    );
  }

  const onChangeHandler = _debounce((event) => {
    const trimmedSearchTerm = event.target.value.trim();
    setSearchTerm(trimmedSearchTerm);
    setDebouncedSearchTerm(trimmedSearchTerm)

  }, 1000)

  useEffect(() => {
    if (state.faqs.search_results.length === 0) {
      setNoOptionsText(fabLabel.noOptionsText)
    } else {
      setNoOptionsText('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.search_results])

  const loadMoreOptions = () => {
    dispatch(getFAQs({ query: searchTerm, page: page + 1, lang: languageMapper[i18n.language] }))
    setPage((prev) => prev + 1)
  };

  const handleInputChange = (event, value) => {
    setIsTyping(value.length > 0);
    flagRef.current = true;
    if (value.trim().length === 0) {
      clearSearch()
    }
  };
  const handleOptionSelected = (event: React.ChangeEvent<{}>, value: any) => {
    flagRef.current = false;
    setOptionsHeight(0)
    handleSearchCallback(value)
    setSearchTerm(value)
    setDebouncedSearchTerm(value)
  };

  const getWidth = () => {
    if (!isTyping && state.faqs.search_results?.length) {
      if (isXS) return '100%';
      if (isMD) return '125% !important';
      if (isSM) return '140% !important';
      return '135% !important';
    }
    return '100%';
  };
  
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        marginTop: "24px",
        width: "100%",
      }}
    >
      <Autocomplete
        freeSolo={searchTerm?.length ? false : true}
        id="free-solo-2-demo"
        
        onInputChange={handleInputChange}
        onChange={handleOptionSelected}
        loading={state.loading}
        forcePopupIcon={false}
        value={searchTerm}
        sx={{
          width: '100%',

        }}
        noOptionsText={<NoOptionsText />}
        loadingText={state.loading && <div className="loading-container">
          <img alt="" src={spinnerImg} className="loader" aria-hidden="true" />
          <p className="loading-text">{fabLabel.loadingMessage}"<b>{searchTerm}</b>" ...</p>
        </div>}
        filterOptions={filterOptions}
        disableClearable={true}
        options={state.auto?.length ? state.auto : state.faqs?.search_results.length ? state.faqs?.search_results?.map((item) => item.title) : []}
        PopperComponent={(props) => (
          <Popper {...props}
          id="autocomplete-popper"
          popperOptions={
            {
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: [],
                  },
                },
              ],
            }
          }
            sx={{
              paddingTop: '0.5rem',
              zIndex: '3 !important',
              '& .MuiAutocomplete-paper': {
                border: '1px solid #B8DCFF',
                boxShadow: 'none',
                borderRadius: '10px !important',
                width: getWidth(),

              },
              '& .MuiAutocomplete-option': {
                p: '0 !important'
              },
              '& .MuiAutocomplete-listbox': {
                position: 'static',
                p: isTyping ? '12px 24px 12px 24px' : 0,
               
              },
              '& .MuiAutocomplete-option[aria-selected=true]': {
                bgcolor: 'transparent !important',
              }
            }}

          />
        )}


        renderOption={(props, option: any) => {
          const { ...optionProps } = props;
          return (
            <Box>
              {!isTyping && props?.['data-option-index'] === 0 && <li style={{ fontSize: '16px', backgroundColor: '#EFF0F2', fontWeight: 600, padding: '18px 24px', marginBottom: '14px' }}>{`${fabLabel.foundResultsText[0]} ${searchCount} ${fabLabel.foundResultsText[1]}`}</li>}
              <Box
                component="li"
                sx={{
                  '& > img': { mr: '16px', flexShrink: 0, },
                  m: !isTyping ? '0 24px 0 24px' : '0',

                }}
                {...optionProps}
                display={'flex'}
              >
                <Box sx={{
                  py: '12px !important',
                  '& > img': { mr: isTyping ? '16px' : 0, flexShrink: 0 }, display: 'flex', flexDirection: 'row', alignItems: 'center', '&:hover': {
                    color: '#1460AA',

                  }
                }}>
                  {isTyping && <img alt="" src={searchIcon} aria-hidden="true" />}
                  <Typography fontSize={'16px'} fontWeight={400} noWrap dangerouslySetInnerHTML={{ __html: isTyping ? option.replace(new RegExp(`(${searchTerm})`, 'gi'), (match) => `<span class='highlighted-search'>${match}</span>`) : option }} />
                </Box>
              </Box>
              {!isTyping && <Divider sx={{ m: !isTyping ? '0 24px 0 24px' : '0', }} variant="fullWidth" />}
            </Box>
          )
        }}
        renderInput={(params) => (
          <TextField
          onClick={
            () => {
              flagRef.current = true;
              setOptionsHeight(prevOptionsHeight.current)
            }
          }
          onFocus={() => {flagRef.current = true; setOptionsHeight(prevOptionsHeight.current)}}
          onBlur={() => {flagRef.current = false; setOptionsHeight(0)}}
            placeholder={fabLabel.searchPlaceholder}
            onChange={(e) => onChangeHandler(e)}
            sx={{
              width: "inherit",
              '& ::placeholder': { fontSize: '16px', color: '#717173', fontWeight: 600, opacity: 1 },
              '& .MuiOutlinedInput-root': {
                py: 0,
                px: '16px',
                '& fieldset': {
                  borderColor: '#B8DCFF', // Border color
                },
                '&:hover fieldset': {
                  borderColor: '#B8DCFF', // Hover border color
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#B8DCFF', // border color when focused
                  borderWidth: 'thin'
                },
              },
              marginTop: "auto",
              height: "48px",
              '& .MuiInputBase-input': {
                fontWeight: '600',
                fontSize: '16px',
                color: (state.loading === false && filterError && searchTerm.length && noOptionsText.length) ? '#D8292F' : '#000',
              },
            }}
            onKeyDown={(e) => handleEnterPressSearch(e)}
            {...params}
            InputProps={{
              sx: { borderRadius: '10px', background: 'white', height: '48px' },
              ...params.InputProps,
              startAdornment: (
                <>
                  {(state.loading === false && filterError && searchTerm.length && noOptionsText.length) ? <InputAdornment position="start">
                    <img alt="" src={searchRedIcon} aria-hidden="true" />
                  </InputAdornment> : <InputAdornment position="start">
                    <img alt="" src={searchIcon} aria-hidden="true" />
                  </InputAdornment>}
                </>
              ),
              endAdornment: (
                <>
                  {searchTerm.length ? <InputAdornment
                    sx={{ cursor: "pointer" }}

                    position="end">
                    <IconButton onClick={clearSearch} sx={{}}>
                      <img alt="" src={state.loading === false && searchTerm.length && !(state.auto.length || state.faqs.search_results.length) ? redCloseIcon : closeIcon} height={'14px'} aria-hidden="true" />
                    </IconButton>
                  </InputAdornment> : null}
                </>
              ),
            }}
          />
        )}
        ListboxComponent={(listboxProps) => (
          <CustomListboxComponent {...listboxProps} loadMore={loadMoreOptions} currentPage={state.faqs?.page} totalPages={state.faqs?.total_pages} label={fabLabel.loadMoreText} isTyping={isTyping} />
        )}
      />
    </Box>
  );
}

export default FAQSearchBar;
